import { coarsen, type LatLon } from '@repo/shared/geo';
import { readJson, writeJson } from './storage.ts';

export interface DevLocation {
  id: string;
  label: string;
  lat: number;
  lon: number;
  country: string;
}

/**
 * Test-Standorte für die Entwicklung. Pro Tab wählbar (sessionStorage), damit sich Umkreis
 * und Land mit mehreren Tabs auf einem Rechner testen lassen.
 */
export const DEV_LOCATIONS: readonly DevLocation[] = [
  { id: 'berlin', label: 'Berlin', lat: 52.52, lon: 13.405, country: 'DE' },
  { id: 'potsdam', label: 'Potsdam (27 km von Berlin)', lat: 52.39, lon: 13.065, country: 'DE' },
  { id: 'muenchen', label: 'München', lat: 48.137, lon: 11.575, country: 'DE' },
  { id: 'wien', label: 'Wien', lat: 48.208, lon: 16.373, country: 'AT' },
  { id: 'zuerich', label: 'Zürich', lat: 47.377, lon: 8.541, country: 'CH' },
  { id: 'newyork', label: 'New York', lat: 40.713, lon: -74.006, country: 'US' },
];

const DEV_KEY = 'app.devLocation.v1';

export function loadDevLocation(): DevLocation | null {
  if (!import.meta.env.DEV) return null;
  const id = readJson<string>('session', DEV_KEY);
  return DEV_LOCATIONS.find((location) => location.id === id) ?? null;
}

export function saveDevLocation(id: string | null): void {
  writeJson('session', DEV_KEY, id);
}

export type LocationProblem = 'unsupported' | 'denied' | 'unavailable';

export class LocationError extends Error {
  readonly problem: LocationProblem;

  constructor(problem: LocationProblem) {
    super(locationProblemText(problem));
    this.name = 'LocationError';
    this.problem = problem;
  }
}

/** Browser-Standort – wird nur für den Umkreis-Filter abgefragt und auf ~1 km gerundet. */
export function requestBrowserLocation(timeoutMs = 10_000): Promise<LatLon> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new LocationError('unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve(coarsen({ lat: position.coords.latitude, lon: position.coords.longitude })),
      (error) =>
        reject(
          new LocationError(error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable'),
        ),
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 10 * 60_000 },
    );
  });
}

export function locationProblemText(problem: LocationProblem): string {
  switch (problem) {
    case 'denied':
      return 'Du hast den Standort nicht freigegeben. Erlaube ihn in der Adressleiste oder such im ganzen Land oder weltweit.';
    case 'unsupported':
      return 'Dein Browser unterstützt keine Standortabfrage.';
    case 'unavailable':
      return 'Dein Standort konnte gerade nicht ermittelt werden.';
  }
}
